import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useFavouriteTeams } from "../../Data/useFavouriteTeams";
import {
  persistPendingFavouriteTeam,
  signupPathForFavouriteTeam,
} from "../../utils/pendingFavouriteTeam";

type Props = {
  teamName: string;
  /** Pixel size of the star. */
  size?: number;
  className?: string;
  /** Colour of the filled/outlined star. Defaults to gold. */
  color?: string;
};

const HOVER_MQ = "(hover: hover) and (pointer: fine)";
const TOOLTIP_GAP_PX = 8;
const TOOLTIP_VIEWPORT_PAD_PX = 8;
const TOUCH_TOOLTIP_MS = 4500;

function useCanHover(): boolean {
  const [canHover, setCanHover] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia(HOVER_MQ).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(HOVER_MQ);
    const apply = () => setCanHover(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  return canHover;
}

function favouriteStarWillDoHint(canFavourite: boolean, active: boolean, name: string): string {
  if (!canFavourite) return `Sign up to follow ${name}`;
  return active ? `Remove ${name} from your favourite teams` : `Add ${name} to your favourite teams`;
}

type TooltipCoords = { top: number; left: number };

/**
 * Star toggle to favourite a team in-context (match cards / scoreboards).
 * Logged-out users see the empty star and are sent to sign up to follow the team.
 * Stops click propagation so it never triggers the surrounding card/link navigation.
 * Desktop: tooltip on hover. Mobile: tooltip on tap (confirmation after toggle).
 */
export function FavouriteTeamStar({
  teamName,
  size = 24,
  className = "",
  color = "#FFC02E",
}: Props) {
  const { canFavourite, isFavourite, toggle } = useFavouriteTeams();
  const navigate = useNavigate();
  const location = useLocation();
  const canHover = useCanHover();
  const [busy, setBusy] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [touchMessage, setTouchMessage] = useState<string | null>(null);
  const [coords, setCoords] = useState<TooltipCoords | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const lastPointerTypeRef = useRef<string>("mouse");
  const tooltipId = useId();
  const name = String(teamName || "").trim();

  const active = canFavourite && isFavourite(name);
  const willDoHint = favouriteStarWillDoHint(canFavourite, active, name);
  const tooltipText = touchMessage ?? willDoHint;

  const updateTooltipPosition = useCallback(() => {
    const btn = btnRef.current;
    const tip = tooltipRef.current;
    if (!btn || !tip) return;
    const br = btn.getBoundingClientRect();
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;
    const maxLeft = Math.max(
      TOOLTIP_VIEWPORT_PAD_PX,
      window.innerWidth - tw - TOOLTIP_VIEWPORT_PAD_PX
    );
    const left = Math.min(Math.max(br.left + br.width / 2 - tw / 2, TOOLTIP_VIEWPORT_PAD_PX), maxLeft);
    let top = br.top - th - TOOLTIP_GAP_PX;
    if (top < TOOLTIP_VIEWPORT_PAD_PX) {
      top = br.bottom + TOOLTIP_GAP_PX;
    }
    setCoords({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!tooltipOpen) {
      setCoords(null);
      return;
    }
    updateTooltipPosition();
  }, [tooltipOpen, tooltipText, updateTooltipPosition]);

  useEffect(() => {
    if (!tooltipOpen) return;
    const onReposition = () => updateTooltipPosition();
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [tooltipOpen, updateTooltipPosition]);

  useEffect(() => {
    if (!tooltipOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (btnRef.current?.contains(target) || tooltipRef.current?.contains(target)) return;
      setTooltipOpen(false);
      setTouchMessage(null);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setTooltipOpen(false);
        setTouchMessage(null);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [tooltipOpen]);

  useEffect(() => {
    if (!tooltipOpen || canHover) return;
    const t = window.setTimeout(() => {
      setTooltipOpen(false);
      setTouchMessage(null);
    }, TOUCH_TOOLTIP_MS);
    return () => window.clearTimeout(t);
  }, [tooltipOpen, canHover, tooltipText]);

  const goToSignup = useCallback(() => {
    persistPendingFavouriteTeam(name);
    const back = `${location.pathname}${location.search}${location.hash}`;
    navigate(signupPathForFavouriteTeam(name, back));
  }, [name, navigate, location.pathname, location.search, location.hash]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    lastPointerTypeRef.current = e.pointerType || "mouse";
    e.stopPropagation();
  }, []);

  const onClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (busy || !name) return;

      const coarseTap = lastPointerTypeRef.current === "touch" || !canHover;

      if (!canFavourite) {
        if (coarseTap && !tooltipOpen) {
          setTouchMessage(`${willDoHint}. Tap again to continue.`);
          setTooltipOpen(true);
          return;
        }
        setTooltipOpen(false);
        setTouchMessage(null);
        goToSignup();
        return;
      }

      const adding = !active;
      setBusy(true);
      try {
        await toggle(name);
      } finally {
        setBusy(false);
      }

      if (coarseTap) {
        setTouchMessage(
          adding ? `Added ${name} to your favourite teams` : `Removed ${name} from your favourite teams`
        );
        setTooltipOpen(true);
      }
    },
    [busy, name, canHover, canFavourite, tooltipOpen, willDoHint, goToSignup, active, toggle]
  );

  if (!name) return null;

  const guestLabel = `Sign up to follow ${name}`;
  const ariaLabel = canFavourite
    ? active
      ? `Remove ${name} from favourite teams`
      : `Add ${name} to favourite teams`
    : guestLabel;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={onClick}
        onPointerDown={onPointerDown}
        onPointerEnter={() => {
          if (canHover) {
            setTouchMessage(null);
            setTooltipOpen(true);
          }
        }}
        onPointerLeave={() => {
          if (canHover) setTooltipOpen(false);
        }}
        onFocus={() => {
          if (canHover) {
            setTouchMessage(null);
            setTooltipOpen(true);
          }
        }}
        onBlur={() => {
          if (canHover) setTooltipOpen(false);
        }}
        aria-pressed={active}
        aria-label={ariaLabel}
        aria-describedby={tooltipOpen ? tooltipId : undefined}
        className={`inline-flex shrink-0 items-center justify-center rounded-full p-0.5 leading-none transition-opacity hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-current disabled:opacity-60 ${className}`.trim()}
        disabled={busy}
      >
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill={active ? color : "none"}
          stroke={color}
          strokeWidth={active ? 0 : 2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M12 2.5l2.9 5.88 6.49.94-4.7 4.58 1.11 6.46L12 17.9l-5.8 3.05 1.11-6.46-4.7-4.58 6.49-.94L12 2.5z" />
        </svg>
      </button>
      {tooltipOpen && typeof document !== "undefined"
        ? createPortal(
            <div
              ref={tooltipRef}
              id={tooltipId}
              role="tooltip"
              style={{
                top: coords?.top ?? 0,
                left: coords?.left ?? 0,
                visibility: coords ? "visible" : "hidden",
              }}
              className="pointer-events-none fixed z-[10030] max-w-[min(240px,calc(100vw-16px))] rounded-md bg-[#1B062F] px-2.5 py-1.5 text-center font-chakra text-[11px] font-semibold leading-snug text-white shadow-[0_4px_16px_rgba(0,0,0,0.45)] ring-1 ring-white/15"
            >
              {tooltipText}
            </div>,
            document.body
          )
        : null}
    </>
  );
}
